import { Coupon } from "../../../models/inventory/Coupon";
import { CouponFilters } from "../../../models/inventory/CouponsFilters";
import {
  APIBlobResponse,
  APIResponse,
  PaginatedAPIResponse,
} from "../../../models/NetworkResponse";
import { getAPI, downloadCSV, postAPI, putAPI } from "../../../api/Networking";

export type CouponActivityAction =
  | "DELETE"
  | "EXPIRE"
  | "UPDATE_EXPIRY_DATE"
  | "REACTIVATE";

export interface CouponActivityChange {
  active: 0 | 1;
  expired: 0 | 1;
  expiry_date: string | null;
}

export interface CouponActivityLog {
  id: number;
  coupon_id: number;
  order_id: number;
  action: CouponActivityAction;
  action_label: string;
  admin_id: number;
  admin_name: string | null;
  platform: string | null;
  created_date: string;
  changes: {
    before: CouponActivityChange;
    after: CouponActivityChange;
  } | null;
}

export interface CouponActivityLogFilters {
  page_no: number;
  limit: number;
  coupon_id?: number;
  order_id?: number;
  action?: CouponActivityAction;
}

export default class CouponsAPI {
  parseFilters = (filters: CouponFilters): URLSearchParams => {
    const queryParams = new URLSearchParams();

    if (filters.page_no)
      queryParams.append("page_no", filters.page_no.toString());

    if (filters.limit) queryParams.append("limit", filters.limit.toString());

    const searchText = filters.search_text?.trim();
    if (searchText) queryParams.append("search_text", searchText);

    if (filters.coupon_id !== undefined && filters.coupon_id !== null) {
      const couponIds = Array.isArray(filters.coupon_id)
        ? filters.coupon_id.filter((id) => Number(id) > 0)
        : [filters.coupon_id];
      if (couponIds.length > 0) {
        queryParams.append("coupon_id", couponIds.join(","));
      }
    }

    if (filters.order_id !== undefined && filters.order_id !== null) {
      const orderIds = Array.isArray(filters.order_id)
        ? filters.order_id.filter((id) => Number(id) > 0)
        : [filters.order_id];
      if (orderIds.length > 0) {
        queryParams.append("order_id", orderIds.join(","));
      }
    }

    if (filters.inventory_id !== undefined && filters.inventory_id !== null) {
      const inventoryIds = Array.isArray(filters.inventory_id)
        ? filters.inventory_id.filter((id) => Number(id) > 0)
        : [filters.inventory_id];
      if (inventoryIds.length > 0) {
        queryParams.append("inventory_id", inventoryIds.join(","));
      }
    }

    if (filters.customer_id !== undefined && filters.customer_id !== null) {
      const customerIds = Array.isArray(filters.customer_id)
        ? filters.customer_id.filter((id) => Number(id) > 0)
        : [filters.customer_id];
      if (customerIds.length > 0) {
        queryParams.append("customer_id", customerIds.join(","));
      }
    }

    if (filters.status)
      queryParams.append("status", filters.status.toString());

    if (filters.redeemed === true || Number(filters.status) === 5)
      queryParams.append("redeemed", "true");

    if (filters.active) queryParams.append("active", filters.active.toString());

    if (filters.is_onelink !== undefined)
      queryParams.append("is_onelink", filters.is_onelink.toString());

    if (filters.sort_by)
      queryParams.append("sort_by", filters.sort_by.toString());

    if (filters.sort_type)
      queryParams.append("sort_type", filters.sort_type.toString());

    return queryParams;
  };

  getCoupons = async (
    filters: CouponFilters,
    downloadReport: boolean
  ): Promise<PaginatedAPIResponse<Coupon> | APIBlobResponse> => {
    try {
      const queryParams = this.parseFilters(filters);

      if (downloadReport) {
        queryParams.append("download_report", true.toString());
        const response = await downloadCSV(
          `/v5/coupons?${queryParams.toString()}`
        );
        return response;
      } else {
        const response = await getAPI(`/v5/coupons?${queryParams.toString()}`);

        return response.result;
      }
    } catch (error) {
      throw error;
    }
  };

  getCouponsShipments = async (
    filters: CouponFilters,
    downloadReport: boolean
  ): Promise<PaginatedAPIResponse<Coupon> | APIBlobResponse> => {
    try {
      const queryParams = this.parseFilters(filters);
      const response = await downloadCSV(
        `/v5/coupons/download-shipments-report?${queryParams.toString()}`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  sendReminders = async (couponIds: number[]): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/coupons/send-reminders`, {
        coupon_id: couponIds,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  reactivateCoupons = async (
    filters: CouponFilters,
    couponIds: number[]
  ): Promise<APIResponse> => {
    try {
      const queryParams = this.parseFilters(filters);

      const response = await putAPI(
        `/v5/coupons/reactivate?${queryParams.toString()}`,
        {
          coupon_ids: couponIds,
        }
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  expireCoupons = async (
    filters: CouponFilters,
    couponIds: number[]
  ): Promise<APIResponse> => {
    try {
      const queryParams = this.parseFilters(filters);

      const response = await putAPI(
        `/v5/coupons/expire?${queryParams.toString()}`,
        {
          coupon_ids: couponIds,
        }
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getCouponJob = async (jobId: number): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v1/redemptions/admin/coupons/jobs/${jobId}`,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  reactivateAllOnOrder = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await putAPI(
        `/v5/coupons/reactivate?order_id=${orderId}`,
        { all_selected: true },
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  expireAllOnOrder = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/coupons/expire?order_id=${orderId}`, {
        all_selected: true,
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  deleteCoupons = async (
    orderId: number,
    couponIds: number[]
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v1/redemptions/admin/coupons/delete`, {
        order_id: orderId,
        coupon_ids: couponIds,
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  deleteAllOnOrder = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v1/redemptions/admin/coupons/delete`, {
        order_id: orderId,
        all_selected: true,
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateExpiryDate = async (
    orderId: number,
    couponIds: number[],
    expiryDate: string
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(
        `/v1/redemptions/admin/coupons/expiry-date`,
        {
          order_id: orderId,
          coupon_ids: couponIds,
          expiry_date: expiryDate,
        }
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getActivityLogs = async (
    filters: CouponActivityLogFilters
  ): Promise<PaginatedAPIResponse<CouponActivityLog>> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page_no", String(filters.page_no));
      queryParams.append("limit", String(filters.limit));
      if (filters.coupon_id)
        queryParams.append("coupon_id", String(filters.coupon_id));
      if (filters.order_id)
        queryParams.append("order_id", String(filters.order_id));
      if (filters.action) queryParams.append("action", filters.action);

      const response = await getAPI(
        `/v1/redemptions/admin/coupons/activity-logs?${queryParams.toString()}`
      );
      return response.result as PaginatedAPIResponse<CouponActivityLog>;
    } catch (error: any) {
      throw error;
    }
  };
}
